<template>
  <div class="ip-info-detector">
    <PrivacyScore :ipInfo="privacyIpInfo" :browserInfo="browserInfo" />
    <div class="loading" v-if="loading">
      <div class="spinner"></div>
      <p>{{ t('ipInfo.loading') }}</p>
    </div>
    <div class="error" v-else-if="error">
      <p>{{ error }}</p>
      <button @click="fetchIPInfo">{{ t('ipInfo.retry') }}</button>
    </div>
    <div class="ip-info" v-else>
      <div class="info-card">
        <div class="info-item">
          <div class="info-label">{{ t('ipInfo.ipAddress') }}:</div>
          <div class="info-value">{{ ipInfo.ip }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('ipInfo.location') }}:</div>
          <div class="info-value">{{ ipInfo.country }} {{ ipInfo.city }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('ipInfo.isp') }}:</div>
          <div class="info-value">{{ ipInfo.isp }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('ipInfo.asNumber') }}:</div>
          <div class="info-value">{{ ipInfo.as || t('ipInfo.notDetected') }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('ipInfo.ipVersion') }}:</div>
          <div class="info-value">IPv{{ ipInfo.ipVersion }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">{{ t('ipInfo.timezone') }}:</div>
          <div class="info-value">{{ ipInfo.timezone || t('ipInfo.notDetected') }}</div>
        </div>
      </div>
      
      <!-- <button @click="fetchIPInfo" class="refresh-btn">{{ t('ipInfo.refresh') }}</button> -->
    </div>
  </div>
</template>

<script>
import PrivacyScore from './PrivacyScore.vue';
import { useI18n } from 'vue-i18n';
import DNSLeakDetector from './DNSLeakDetector';
// import WebRTCLeakDetector from './WebRTCLeakDetector';
// import BrowserFingerprintDetector from './BrowserFingerprintDetector';
import GeoLocationMatcher from './GeoLocationMatcher';

export default {
  name: 'IPInfoDetector',
  components: {
    PrivacyScore
  },
  setup() {
    const { t } = useI18n();
    
    return { t };
  },
  data() {
    return {
      ipInfo: {
        ip: '',
        country: '',
        city: '',
        isp: '',
        as: '',
        ipVersion: 4,
        timezone: '',
        usingProxy: false,
        hasDNSLeak: false
      },
      browserInfo: {
        cookiesEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack === "1" || window.doNotTrack === "1",
        webRTCEnabled: typeof RTCPeerConnection !== 'undefined',
        userAgent: navigator.userAgent,
        localStorageEnabled: typeof localStorage !== 'undefined',
        plugins: this.getPlugins(),
        hasRiskyPlugins: this.hasRiskyPlugins()
      },
      loading: true,
      error: null,
      // 检测工具实例
      geoMatcher: new GeoLocationMatcher(),
      dnsLeakDetector: new DNSLeakDetector()
    }
  },
  mounted() {
    this.fetchIPInfo();
    // Detect WebRTC IPs for privacy scoring
    this.detectWebRTCIPs().then(ips => {
      this.browserInfo.webRTCIPs = ips;
    });
    // 进行 DNS 泄露检测
    this.checkDNSLeak();
  },
  methods: {
    fetchIPInfo() {
      this.loading = true;
      this.error = null;
      
      // 使用多个API获取IP信息以提高准确性
      this.fetchFromIpInfo()
        .then((ipApiData) => {
          // 合并数据并设置IP信息
          this.ipInfo = {
            ip: ipApiData.query || '未检测到',
            country: ipApiData.country || '未检测到',
            countryCode: ipApiData.countryCode,
            city: ipApiData.city || '',
            isp: ipApiData.isp || '未检测到',
            as: ipApiData.as || '未检测到',
            usingProxy: ipApiData.proxy || false,
            hosting: ipApiData.hosting || false,
            ipVersion: this.getIpVersion(ipApiData.query),
            timezone: ipApiData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            hasDNSLeak: this.ipInfo.hasDNSLeak
          };
          
          // 在获取 IP 信息后再次检查 DNS 泄露
          this.checkDNSLeak();
        })
        .catch(err => {
          this.error = `${this.t('ipInfo.fetchError')}: ${err.message}`;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 从ip-api.com获取IP信息
    fetchFromIpInfo() {
      return fetch('https://ip.nezhavpn.uk/ip-api')
        .then(response => {
          if (!response.ok) {
            throw new Error('无法从ip-api.com获取IP信息');
          }
          return response.json();
        });
    },
    
    // 检测WebRTC IP
    async detectWebRTCIPs() {
      return new Promise((resolve) => {
        const ips = [];
        
        // 尝试使用RTCPeerConnection API获取IP地址
        if (typeof RTCPeerConnection !== 'undefined') {
          try {
            const pc = new RTCPeerConnection({
              iceServers: [
                { urls: 'stun:stun.l.google.com:19302' }
              ]
            });
            
            pc.onicecandidate = (event) => {
              if (event.candidate) {
                // 提取IP地址
                const match = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/.exec(event.candidate.candidate);
                if (match && match[1]) {
                  if (!ips.includes(match[1])) {
                    ips.push(match[1]);
                  }
                }
              } else {
                // 收集完成
                resolve(ips);
              }
            };
            
            // 创建一个数据通道以触发ICE收集
            pc.createDataChannel('');
            
            // 开始连接过程
            pc.createOffer().then(offer => {
              return pc.setLocalDescription(offer);
            }).catch(error => {
              console.error('WebRTC发生错误:', error);
              resolve(ips);
            });
            
            // 设置超时
            setTimeout(() => {
              resolve(ips);
              try {
                pc.close();
              } catch (e) {
                // Ignore errors when closing peer connection
                console.debug('Error closing peer connection:', e);
              }
            }, 2000);
          } catch (error) {
            console.error('检测WebRTC IP时出错:', error);
            resolve(ips);
          }
        } else {
          resolve(ips);
        }
      });
    },
    
    getIpVersion(ip) {
      // 简单判断IPv4还是IPv6
      return (ip && ip.includes(':')) ? 6 : 4;
    },
    
    getPlugins() {
      // 获取浏览器插件列表
      if (navigator.plugins && navigator.plugins.length) {
        return Array.from(navigator.plugins).map(p => p.name);
      }
      return [];
    },
    
    hasRiskyPlugins() {
      // 检测是否存在隐私风险高的插件
      const riskyPluginKeywords = ['flash', 'silverlight', 'java', 'unity'];
      const plugins = this.getPlugins();
      
      return plugins.some(plugin => {
        const pluginName = plugin.toLowerCase();
        return riskyPluginKeywords.some(keyword => pluginName.includes(keyword));
      });
    },
    
    // 检测 DNS 泄露
    async checkDNSLeak() {
      try {
        const dnsResult = await this.dnsLeakDetector.detectLeaks();
        // 更新 DNS 泄露状态
        this.ipInfo.hasDNSLeak = dnsResult.leakDetected;
      } catch (error) {
        console.error('DNS 泄露检测失败:', error);
        // 检测失败时默认为无泄露
        this.ipInfo.hasDNSLeak = false;
      }
    }
  },
  computed: {
    privacyIpInfo() {
      return {
        ...this.ipInfo
      };
    }
  }
}
</script>

<style scoped>
/* .ip-info-detector {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
} */

h2 {
  color: #1e3a8a;
  margin-bottom: 20px;
}

.info-card {
  background-color: white;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  margin-bottom: 8px;
  padding: 6px;
  border-bottom: 1px solid #eee;
}

.info-label {
  font-weight: bold;
  width: 120px;
  color: #555;
}

.info-value {
  flex: 1;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #1e3a8a;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #e53e3e;
  padding: 12px;
  background-color: #fff5f5;
  border-radius: 8px;
  margin-bottom: 12px;
}

.refresh-btn, .test-btn {
  background-color: #1e3a8a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-right: 10px;
}

.test-btn {
  background-color: #2a4caf;
}

.refresh-btn:hover, .test-btn:hover {
  opacity: 0.9;
}

.test-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
